import { get, set } from 'idb-keyval';
import { readMediaInfo, type EmbeddedPicture, type MediaChapter, type RangeReader } from '../media';
import type { Book, Chapter, Track } from '../types';

/**
 * What each audio file says about itself — chapters, cover art, length — read
 * once and kept, so no file is ever parsed twice.
 *
 * The keys are versioned. Version 1 (`shelf:chapters:<id>`) came from a parser
 * that only understood Nero chapters and cached its empty results too, so
 * books whose chapters were in a QuickTime chapter track stayed at "one
 * chapter" forever. A new key has every file read again by the fuller parser.
 */

export interface TrackMedia {
  chapters: MediaChapter[];
  durationSec?: number;
  hasCover: boolean;
}

/** Above this many files, a book's chapters are its files: reading every tag
 *  in a 60-file folder costs more than it could add. */
const MAX_FILES_TO_READ = 12;
const COVER_MAX_PX = 640;

const infoKey = (t: Track) => `shelf:media:v2:${t.id}:${t.sizeBytes}`;
const coverKey = (t: Track) => `shelf:cover:v2:${t.id}:${t.sizeBytes}`;

const inflight = new Map<string, Promise<TrackMedia | null>>();
/** Covers stay usable for the session even where IndexedDB is unavailable. */
const coverMemory = new Map<string, Blob>();

/**
 * Metadata for one file. Null when it could not be read this time (a network
 * error, say) — not cached, so it is tried again later.
 */
export function trackMedia(track: Track, read: RangeReader): Promise<TrackMedia | null> {
  const key = infoKey(track);
  let pending = inflight.get(key);
  if (!pending) {
    pending = loadTrackMedia(track, read).finally(() => inflight.delete(key));
    inflight.set(key, pending);
  }
  return pending;
}

async function loadTrackMedia(track: Track, read: RangeReader): Promise<TrackMedia | null> {
  try {
    const cached = await get<TrackMedia>(infoKey(track));
    if (cached) return cached;
  } catch {
    /* no cache: read the file */
  }

  let info;
  try {
    info = await readMediaInfo(read, track.sizeBytes);
  } catch {
    return null;
  }

  const cover = info.cover ? await shrinkCover(info.cover) : null;
  const record: TrackMedia = {
    chapters: info.chapters,
    ...(info.durationSec ? { durationSec: info.durationSec } : {}),
    hasCover: cover !== null,
  };

  if (cover) coverMemory.set(coverKey(track), cover);
  try {
    if (cover) await set(coverKey(track), cover);
    await set(infoKey(track), record);
  } catch {
    /* kept in memory for this session */
  }
  return record;
}

export async function coverBlob(track: Track): Promise<Blob | null> {
  const key = coverKey(track);
  const remembered = coverMemory.get(key);
  if (remembered) return remembered;
  try {
    const stored = await get<Blob>(key);
    if (stored) coverMemory.set(key, stored);
    return stored ?? null;
  } catch {
    return null;
  }
}

/**
 * A book's chapters, from the marks embedded in its files.
 *
 * Each file contributes its own chapters when it has more than one, and is a
 * chapter by itself otherwise — so a single .m4b, a folder of per-chapter
 * MP3s, and a book split into "Part 1.m4b" and "Part 2.m4b" with chapters in
 * each all come out right.
 */
export async function bookChapters(
  book: Book,
  read: (track: Track, start: number, end: number) => Promise<ArrayBuffer>,
): Promise<Chapter[]> {
  const perFile = book.tracks.map((t, i) => ({ title: t.name, trackIndex: i, startSec: 0 }));
  if (book.tracks.length > MAX_FILES_TO_READ) return perFile;

  const media = await Promise.all(
    book.tracks.map((t) => trackMedia(t, (start, end) => read(t, start, end))),
  );

  return book.tracks.flatMap((track, i) => {
    const own = media[i]?.chapters ?? [];
    if (own.length < 2) return [{ title: track.name, trackIndex: i, startSec: 0 }];
    return own.map((c) => ({ title: c.title, trackIndex: i, startSec: c.startSec }));
  });
}

/**
 * Embedded art is often a 3000-pixel PNG. Scaled to cover size and stored as
 * JPEG it is a few dozen kilobytes, which matters across a whole library.
 */
async function shrinkCover(picture: EmbeddedPicture): Promise<Blob | null> {
  // A copy: the parsed bytes are a view into a larger read buffer.
  const original = new Blob([picture.data.slice()], { type: picture.mimeType });
  if (typeof createImageBitmap !== 'function') return original;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(original);
  } catch {
    return null; // not an image the browser can show, so not worth keeping
  }

  const scale = Math.min(1, COVER_MAX_PX / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && original.size < 200_000) return original;
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  try {
    if (typeof OffscreenCanvas === 'function') {
      const canvas = new OffscreenCanvas(width, height);
      canvas.getContext('2d')?.drawImage(bitmap, 0, 0, width, height);
      return await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.86 });
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, width, height);
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.86),
    );
  } catch {
    return original;
  } finally {
    bitmap.close();
  }
}
